import styled, { css } from 'styled-components';
import React, { forwardRef } from 'react';
import _ from 'lodash';

import { formatEstimate, getAfterHoursWindow, formatAfterHoursEstimate } from 'shared/helpers/order-estimates';
import { getScheduledOrderTime } from 'shared/helpers/orders';

import CheckoutSuccessIcon from 'assets/checkout-success-icon';
import { ModalContainer } from 'shared/modals/v2-container';
import { ResetButton } from 'shared/components';
import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import useUser from 'src/hooks/use-user';
import useTranslation from 'src/hooks/use-translation';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import { callNativeMethod } from 'src/mobile-ecomm/hooks/use-mobile-native-bridge';
import GuestCheckoutSuccessSignupForm from './signup-form';

const GuestCheckoutSuccess = forwardRef(({ containerProps }, ref) => {
  const cart = useCart();
  const UI = useUI();
  const { isMobileEcommApp } = UI;
  const User = useUser();
  const { t } = useTranslation();

  const { stashedOrder } = cart;

  const {
    deliveryOption: serviceType,
    dispensary,
    isAfterHoursOrder,
    isInStorePickup,
    isCurbsidePickup,
    isDriveThruPickup,
    isDelivery,
    reservation,
    openInfo,
    openInfoV2,
  } = stashedOrder;
  const { orderTypesConfigV2 } = dispensary;

  const useOrderTypesConfigV2 = useDispensaryFlag(`rollout.use-updated-ordering-configurations`, dispensary.id);
  const openInfoForOrder = useOrderTypesConfigV2 ? openInfoV2 : openInfo;

  const serviceTypeToTextMap = {
    curbsidePickup: `curbside pickup`,
    driveThruPickup: `drive thru pickup`,
  };

  const scheduledOrderingForPickupV2 =
    (isInStorePickup && orderTypesConfigV2?.inStorePickup.enableScheduledOrdering) ||
    (isCurbsidePickup && orderTypesConfigV2?.curbsidePickup.enableScheduledOrdering) ||
    (isDriveThruPickup && orderTypesConfigV2?.driveThruPickup.enableScheduledOrdering);
  const scheduledOrderingForDeliveryV2 = isDelivery && orderTypesConfigV2?.delivery.enableScheduledOrdering;

  const scheduledOrderingEnabled = useOrderTypesConfigV2
    ? scheduledOrderingForPickupV2 || scheduledOrderingForDeliveryV2
    : dispensary.storeSettings?.enableScheduledOrderingForPickup ||
      dispensary.storeSettings?.enableScheduledOrderingForDelivery;

  const shouldShowEstimate =
    !(serviceType === 'delivery' && dispensary.featureFlags?.hideDeliveryEstimate) && !scheduledOrderingEnabled;

  const detailedServiceType = useOrderTypesConfigV2 && serviceType === 'pickup' ? `inStorePickup` : serviceType;

  const estimateTypeText =
    isAfterHoursOrder || detailedServiceType === 'inStorePickup'
      ? `Pickup time: `
      : `Estimated ${serviceTypeToTextMap[detailedServiceType] || detailedServiceType} time: `;

  const estimateTime = isAfterHoursOrder
    ? _.upperFirst(formatAfterHoursEstimate(getAfterHoursWindow(openInfoForOrder, detailedServiceType)))
    : formatEstimate(_.get(cart, `durationEstimates[${serviceType}]`), serviceType);

  let scheduledOrderTime;
  if (reservation) {
    scheduledOrderTime = getScheduledOrderTime({ reservation, timezone: dispensary.timezone });
  }

  function onClose() {
    if (isMobileEcommApp) {
      cart.clearOrder();
      callNativeMethod(`onOrderComplete`);
    }
    UI.viewingGuestOrderSuccess = false;
  }

  return (
    <StyledModalContainer
      ref={ref}
      height={User.hasExternalUser ? 216 : 560}
      width={541}
      p='0'
      data-cy='guest-checkout-success'
      data-test='guest-checkout-success'
      {...containerProps}
    >
      <SuccessContainer>
        <StyledSuccessIcon />
        <OrderSubmitted>{t('checkout.order-submitted', 'Order Submitted!')}</OrderSubmitted>
        {shouldShowEstimate && (
          <React.Fragment>
            <StyledEstimateText fontSize='13px' color='#47494c' mt='20px'>
              {estimateTypeText}
              <strong>{estimateTime}</strong>
            </StyledEstimateText>
          </React.Fragment>
        )}
        {scheduledOrderTime && (
          <React.Fragment>
            <StyledEstimateText fontSize='13px' color='#47494c' mt='20px'>
              {`${_.capitalize(serviceTypeToTextMap[serviceType] || serviceType)} time: `}
              <strong>
                {scheduledOrderTime.prefix} | {scheduledOrderTime.startTime} - {scheduledOrderTime.endTime}
              </strong>
            </StyledEstimateText>
          </React.Fragment>
        )}
      </SuccessContainer>
      {!User.hasExternalUser && (
        <React.Fragment>
          <GuestCheckoutSuccessSignupForm />
          <StyledResetButton onClick={onClose}>{t('guest-checkout-modal.no-thanks', 'No thanks')}</StyledResetButton>
        </React.Fragment>
      )}
    </StyledModalContainer>
  );
});

const StyledModalContainer = styled(ModalContainer)`
  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 0;
    &&& {
      top: 28px;
      ${({ isIframe }) =>
        isIframe
          ? css`
              width: ${({ viewportWidth }) => viewportWidth * 0.85}px;
            `
          : css`
              transform: none;
              width: 85vw;
            `}
    }
  }
`;

const OrderSubmitted = styled.span`
  font-size: 22px;
  color: ${({ theme }) => theme.colors.v2Green};
  font-weight: bold;
  margin-top: 16px;
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 47px 42px 47px;
  img {
    margin-left: 9px;
  }
`;

const StyledSuccessIcon = styled(CheckoutSuccessIcon)`
  margin-left: 9px;
`;

const StyledEstimateText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[30]};
  margin-top: 20px;
  span {
    font-weight: bold;
  }
`;

const StyledResetButton = styled(ResetButton)`
  position: abosolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 37px;
  background-color: ${({ theme }) => theme.colors.blueGrey[90]};
  color: ${({ theme }) => theme.colors.blueGrey[40]};
  font-weight: 600;
  font-size: 13px;
  line-height: 1;
  border-bottom-left-radius: 28px;
  border-bottom-right-radius: 28px;
  z-index: 100;
`;

export default GuestCheckoutSuccess;
